import { useState, useEffect, useCallback } from 'react';
import { contractsApi, documentsApi } from '../lib/api';
import { formatDate, formatFileSize } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { 
    MagnifyingGlass, 
    Folder, 
    Download, 
    Check, 
    X, 
    Eye,
    FileText,
    FilePdf,
    FileDoc,
    FileXls,
    Image
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const getFileIcon = (contentType) => {
    if (contentType?.includes('pdf')) return FilePdf;
    if (contentType?.includes('image')) return Image;
    if (contentType?.includes('word') || contentType?.includes('document')) return FileDoc;
    if (contentType?.includes('sheet') || contentType?.includes('excel')) return FileXls;
    return FileText;
};

const getValidationBadge = (status) => {
    switch (status) {
        case 'approvato':
            return <Badge className="bg-green-100 text-green-800">Approvato</Badge>;
        case 'rifiutato':
            return <Badge className="bg-red-100 text-red-800">Rifiutato</Badge>;
        default:
            return <Badge className="bg-amber-100 text-amber-800">In attesa</Badge>;
    }
};

export default function DocumentsPage() {
    const [contracts, setContracts] = useState([]);
    const [selectedContract, setSelectedContract] = useState('all');
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');

    const loadContracts = useCallback(async () => {
        try {
            const response = await contractsApi.getAll();
            setContracts(response.data);
            if (response.data.length > 0) {
                setSelectedContract('all');
            }
        } catch (error) {
            console.error('Error loading contracts:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    const loadDocuments = useCallback(async (contractId) => {
        try {
            const response = await contractsApi.getDocuments(contractId);
            setDocuments(response.data);
        } catch (error) {
            console.error('Error loading documents:', error);
        }
    }, []);

    const loadAllDocuments = useCallback(async () => {
        try {
            // Load documents from all contracts
            const allDocs = [];
            for (const contract of contracts) {
                const response = await contractsApi.getDocuments(contract.id);
                allDocs.push(...response.data.map(d => ({
                    ...d,
                    contract_number: contract.numero_contratto
                })));
            }
            setDocuments(allDocs);
        } catch (error) {
            console.error('Error loading all documents:', error);
        }
    }, [contracts]);

    useEffect(() => {
        loadContracts();
    }, [loadContracts]);

    useEffect(() => {
        if (selectedContract && selectedContract !== 'all') {
            loadDocuments(selectedContract);
        } else if (selectedContract === 'all') {
            loadAllDocuments();
        }
    }, [selectedContract, loadDocuments, loadAllDocuments]);

    const handleDownload = async (docId, filename) => {
        try {
            const response = await documentsApi.download(docId);
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error('Error downloading:', error);
            toast.error('Errore nel download');
        }
    };

    const handleValidate = async (docId, status) => {
        try {
            await documentsApi.validate(docId, { status });
            toast.success(status === 'approvato' ? 'Documento approvato' : 'Documento rifiutato');
            if (selectedContract === 'all') {
                loadAllDocuments();
            } else {
                loadDocuments(selectedContract);
            }
        } catch (error) {
            console.error('Error validating:', error);
            toast.error('Errore nella validazione');
        }
    };

    const filteredDocuments = documents.filter(d => {
        if (!search) return true;
        const searchLower = search.toLowerCase();
        return (
            d.original_filename?.toLowerCase().includes(searchLower) ||
            d.document_type?.toLowerCase().includes(searchLower)
        );
    });

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="documents-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row gap-4 justify-between">
                <div className="flex flex-1 gap-3">
                    <div className="relative flex-1 max-w-md">
                        <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                            placeholder="Cerca documenti..."
                            data-testid="documents-search-input"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="pl-9"
                        />
                    </div>
                    <Select value={selectedContract} onValueChange={setSelectedContract}>
                        <SelectTrigger className="w-64" data-testid="contract-filter">
                            <SelectValue placeholder="Seleziona contratto" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Tutti i contratti</SelectItem>
                            {contracts.map(c => (
                                <SelectItem key={c.id} value={c.id}>
                                    {c.numero_contratto}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* Documents Table */}
            <Card>
                <CardContent className="p-0">
                    <Table>
                        <TableHeader>
                            <TableRow className="table-header">
                                <TableHead>Documento</TableHead>
                                {selectedContract === 'all' && <TableHead>Contratto</TableHead>}
                                <TableHead>Tipo</TableHead>
                                <TableHead>Dimensione</TableHead>
                                <TableHead>Stato</TableHead>
                                <TableHead>Data</TableHead>
                                <TableHead className="w-32"></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filteredDocuments.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={selectedContract === 'all' ? 7 : 6} className="text-center py-12">
                                        <Folder className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
                                        <p className="text-muted-foreground">Nessun documento trovato</p>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredDocuments.map((doc) => {
                                    const FileIcon = getFileIcon(doc.content_type);
                                    return (
                                        <TableRow 
                                            key={doc.id} 
                                            className="table-row"
                                            data-testid={`document-row-${doc.id}`}
                                        >
                                            <TableCell className="table-cell">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-8 h-8 bg-muted rounded flex items-center justify-center flex-shrink-0">
                                                        <FileIcon className="w-4 h-4 text-muted-foreground" />
                                                    </div>
                                                    <span className="font-medium truncate max-w-[200px]">
                                                        {doc.original_filename}
                                                    </span>
                                                </div>
                                            </TableCell>
                                            {selectedContract === 'all' && (
                                                <TableCell className="table-cell">
                                                    <Badge variant="outline">{doc.contract_number}</Badge>
                                                </TableCell>
                                            )}
                                            <TableCell className="table-cell">
                                                <span className="text-muted-foreground">{doc.document_type}</span>
                                            </TableCell>
                                            <TableCell className="table-cell text-muted-foreground">
                                                {formatFileSize(doc.size)}
                                            </TableCell>
                                            <TableCell className="table-cell">
                                                {getValidationBadge(doc.validation_status)}
                                            </TableCell>
                                            <TableCell className="table-cell text-muted-foreground">
                                                {formatDate(doc.uploaded_at)}
                                            </TableCell>
                                            <TableCell className="table-cell">
                                                <div className="flex items-center gap-1">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleDownload(doc.id, doc.original_filename)}
                                                        title="Scarica"
                                                    >
                                                        <Download className="w-4 h-4" />
                                                    </Button>
                                                    {doc.validation_status === 'pending' && (
                                                        <>
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                className="text-green-600"
                                                                onClick={() => handleValidate(doc.id, 'approvato')}
                                                                title="Approva"
                                                            >
                                                                <Check className="w-4 h-4" />
                                                            </Button>
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                className="text-red-600"
                                                                onClick={() => handleValidate(doc.id, 'rifiutato')}
                                                                title="Rifiuta"
                                                            >
                                                                <X className="w-4 h-4" />
                                                            </Button>
                                                        </>
                                                    )}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        </div>
    );
}
